import { Injectable } from '@angular/core';
import { WebStorageService, FileRecord } from './storage/web-storage.service';
import { NetworkService } from './network.service';
import { UploadService } from './upload.service';
import { Subscription, interval } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

/**
 * Servicio para sincronización automática de FOTOS (no videos)
 */
@Injectable({
  providedIn: 'root',
})
export class PhotoSyncService {
  private syncSubscription?: Subscription;
  private isSyncing = false;
  private readonly SYNC_INTERVAL_MS = 30000; // 30 segundos

  constructor(
    private storageService: WebStorageService,
    private networkService: NetworkService,
    private uploadService: UploadService,
  ) {}

  /**
   * Inicia sincronización automática de fotos
   */
  startAutoSync(): void {
    if (this.syncSubscription) {
      console.log('ℹ️ Auto-sync ya está activo');
      return;
    }

    console.log('🔄 Iniciando auto-sync de fotos cada 30 segundos');

    // Sincronizar cada 30 segundos SI hay internet
    this.syncSubscription = interval(this.SYNC_INTERVAL_MS)
      .pipe(
        filter(() => this.networkService.isOnline() && !this.isSyncing),
        switchMap(() => this.syncPendingPhotos()),
      )
      .subscribe();

    // También sincronizar cuando se detecte que vuelve internet
    this.networkService.getOnlineStatus().subscribe((isOnline) => {
      if (isOnline && !this.isSyncing) {
        console.log('🌐 Internet detectado, sincronizando fotos...');
        this.syncPendingPhotos();
      }
    });
  }

  /**
   * Detiene sincronización automática
   */
  stopAutoSync(): void {
    if (this.syncSubscription) {
      this.syncSubscription.unsubscribe();
      this.syncSubscription = undefined;
      console.log('⏸️ Auto-sync detenido');
    }
  }

  /**
   * Sincroniza fotos pendientes manualmente
   */
  async syncPendingPhotos(): Promise<{ success: number; failed: number }> {
    if (this.isSyncing) {
      console.log('ℹ️ Sincronización ya en progreso');
      return { success: 0, failed: 0 };
    }

    if (!this.networkService.isOnline()) {
      console.log('⚠️ Sin conexión, no se pueden sincronizar fotos');
      return { success: 0, failed: 0 };
    }

    this.isSyncing = true;
    let successCount = 0;
    let failedCount = 0;

    try {
      // Obtener fotos pendientes usando método público
      const pendingPhotos = await this.storageService.getPendingPhotos();

      if (pendingPhotos.length === 0) {
        console.log('✅ No hay fotos pendientes de sincronizar');
        return { success: 0, failed: 0 };
      }

      console.log(`📤 Sincronizando ${pendingPhotos.length} fotos pendientes...`);

      // Subir fotos de 5 en 5 (para no saturar)
      const BATCH_SIZE = 5;
      for (let i = 0; i < pendingPhotos.length; i += BATCH_SIZE) {
        const batch = pendingPhotos.slice(i, i + BATCH_SIZE);

        await Promise.all(
          batch.map(async (file) => {
            try {
              // Generar nombre de archivo con extensión correcta
              const extension = file.metadata.type === 'photo' ? 'jpg' : 'mp4';
              const fileName = `${file.key}.${extension}`;

              // Subir archivo al servidor
              const uploadUrl = await this.uploadService.uploadFile(file.blob, fileName);

              // Marcar como subida
              await this.storageService.markFileAsUploaded(file.key, uploadUrl);

              successCount++;
              console.log(`✅ Foto sincronizada: ${file.key} → ${uploadUrl}`);
            } catch (error) {
              failedCount++;
              console.error(`❌ Error sincronizando foto ${file.key}:`, error);

              // Guardar error en metadata
              const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
              await this.storageService.updateFileSyncError(file.key, errorMsg);
            }
          }),
        );

        // Pequeña pausa entre batches
        if (i + BATCH_SIZE < pendingPhotos.length) {
          await this.delay(1000);
        }
      }

      console.log(`✅ Sincronización completada: ${successCount} exitosas, ${failedCount} fallidas`);
    } catch (error) {
      console.error('❌ Error en sincronización de fotos:', error);
    } finally {
      this.isSyncing = false;
    }

    return { success: successCount, failed: failedCount };
  }

  /**
   * Helper para delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Verifica si hay fotos pendientes
   */
  async hasPendingPhotos(): Promise<boolean> {
    const pendingPhotos = await this.storageService.getPendingPhotos();
    return pendingPhotos.length > 0;
  }

  /**
   * Obtiene cantidad de fotos pendientes
   */
  async getPendingPhotosCount(): Promise<number> {
    const pendingPhotos = await this.storageService.getPendingPhotos();
    return pendingPhotos.length;
  }
}
